import { Box, Card, CardBody, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { BiCalculator, BiMenu, BiMinus, BiPlus } from 'react-icons/bi';
import { RiStackLine } from 'react-icons/ri';

import { EnrichedAsset, ResourceType } from '../_openapi/portfolio-optimizer';
import { useAssets } from '../api/asset';
import { ManualTrade } from '../TradeBlotter/TradeBlotter';
import { useTradeBlotterContext } from '../TradeBlotter/TradeBlotterContext';
import { TradeBudgetModal, useTradeBudgetModalControls } from '../TradeBlotter/TradeBudgetModal';
import { tradeCalculator } from '../TradeBlotter/tradeCalculator';
import { PageHeader } from '../widgets/PageHeader';
import { AsOfDateTag } from '../widgets/Table/AsOfDateTag';
import { CreateWidgetFromTable } from '../widgets/Table/CreateWidgetFromTable';
import { Pagination } from '../widgets/Table/Pagination';
import { Table } from '../widgets/Table/Table';
import { TableControls } from '../widgets/Table/TableControls';
import { usePersistentTableState } from '../widgets/Table/usePersistentTableState';
import { useTableColumnSettings } from '../widgets/Table/useTableColumnSettings';
import { AssetDrawer, useAssetDrawerControls } from './AssetDrawer/AssetDrawer';
import { assetListColumns } from './assetListColumns';

const columnHelper = createColumnHelper<EnrichedAsset>();

export const AssetList = () => {
    const { viewAsset, ...assetDrawerProps } = useAssetDrawerControls();
    const { pagination, setPagination, sorting, setSorting, resetTable, columnFilters, setColumnFilters } =
        usePersistentTableState(assetListColumns);
    const { data: assetsPage, isFetching } = useAssets({ pagination, columnFilters, sorting });
    const { calculateTrade, ...tradeBudgetModalProps } = useTradeBudgetModalControls();
    const { addTrade } = useTradeBlotterContext();

    const columns = useMemo(() => {
        return [
            columnHelper.display({
                id: 'contextMenu',
                cell: ({ row }) => {
                    return (
                        <Menu isLazy>
                            <MenuButton
                                size="xs"
                                as={IconButton}
                                aria-label="Options"
                                icon={<Icon boxSize={3} as={BiMenu} />}
                                w="20px"
                                minW="20px"
                                h="20px"
                                variant="outline"
                                borderRadius="sm"
                            />
                            <Portal>
                                <MenuList>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={RiStackLine} />}
                                        onClick={() => viewAsset(row.original.asset.sId)}
                                    >
                                        Security details
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiMinus} />}
                                        onClick={() => {
                                            addTrade({
                                                asset: {
                                                    sId: row.original.asset.sId,
                                                },
                                                side: 'SELL',
                                                quantity: 0,
                                                orderType: 'LMT',
                                                price: {
                                                    type: tradeCalculator.getPriceTypeFromTradeSide('SELL'),
                                                },
                                            } as ManualTrade);
                                        }}
                                    >
                                        Sell Security
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiPlus} />}
                                        onClick={() => {
                                            addTrade({
                                                asset: {
                                                    sId: row.original.asset.sId,
                                                },
                                                side: 'BUY',
                                                quantity: 0,
                                                orderType: 'LMT',
                                                price: {
                                                    type: tradeCalculator.getPriceTypeFromTradeSide('BUY'),
                                                },
                                            } as ManualTrade);
                                        }}
                                    >
                                        Buy Security
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiCalculator} />}
                                        onClick={() => {
                                            calculateTrade({
                                                asset: { sId: row.original.asset.sId },
                                            } as ManualTrade);
                                        }}
                                    >
                                        Calculate Trade
                                    </MenuItem>
                                </MenuList>
                            </Portal>
                        </Menu>
                    );
                },
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                    isSticky: true,
                },
            }),
            ...assetListColumns,
        ];
    }, [addTrade, calculateTrade, viewAsset]);

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } = useTableColumnSettings('assets');
    const table = useReactTable({
        data: assetsPage?.assets || [],
        columns,
        state: {
            columnVisibility,
            columnOrder,
            pagination,
            sorting,
            columnFilters,
        },
        initialState: {
            columnPinning: {
                left: ['contextMenu'],
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <PageHeader title="Security Master">
                <AsOfDateTag dateString={assetsPage?.asOfDate} />
            </PageHeader>
            <Card>
                <CardBody>
                    <TableControls table={table} onReset={resetTable} resourceType={ResourceType.ASSET}>
                        <Box ml="auto">
                            <CreateWidgetFromTable
                                initialValue={{
                                    type: 'securityMaster',
                                    filters: table.getState().columnFilters,
                                    sort: table.getState().sorting,
                                }}
                            />
                        </Box>
                    </TableControls>
                    <Table table={table} isLoading={isFetching} allowXOverflow />
                    <Pagination table={table} totalItems={assetsPage?.totalNr} />
                </CardBody>
            </Card>
            <AssetDrawer {...assetDrawerProps} />
            <TradeBudgetModal {...tradeBudgetModalProps} />
        </>
    );
};
